import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaBookOpen } from "react-icons/fa";
import "./components.css";

/* ─── SECTION HEADER ──────────────────────────────────────────── */

export const SectionHeader = ({ eyebrow, title, subtitle, action }) => (
    <div className="section-head">
        <div>
            {eyebrow && <span className="eyebrow">{eyebrow}</span>}
            <h2 className="section-title">{title}</h2>
            {subtitle && <p className="section-sub">{subtitle}</p>}
        </div>
        {action}
    </div>
);

/* ─── CONTENT CARD ────────────────────────────────────────────── */
/**
 * The card used by Updates, Policies and Blog alike, on both Home and the list
 * pages. One component, so the three surfaces cannot drift apart.
 *
 * The whole card is a link, but only ONE <a> is focusable (the title), with a
 * ::after overlay making the rest of the card clickable. That keeps the tab order
 * sane — a card that exposes three separate tab stops to the same destination is
 * miserable with a keyboard.
 */
/**
 * `image` is OPTIONAL and opt-in: pass it (as { src }) and the card grows a 16:9
 * thumbnail with the category pill sitting on it; omit it and the card is exactly the
 * text-only card it has always been. That is why updates and policies are untouched.
 *
 * A null src is still a valid image prop — it renders the designed placeholder rather
 * than a blank box, so a post with no images keeps the same card shape as its neighbours.
 *
 * The thumbnail is deliberately NOT its own link: .card-link already stretches over the
 * whole card, so wrapping it would add a duplicate tab stop to the same destination.
 */
/**
 * A missing src is not the only way to have no picture: the file can be deleted or moved
 * after the post was written, and the API still hands us a path. Falling back on error
 * means that degrades to the same designed placeholder instead of the browser's broken
 * image glyph. The alt is empty because the thumbnail is decorative — the title next to
 * it already names the post.
 */
const CardThumb = ({ src, kind }) => {
    const [failed, setFailed] = useState(false);

    return (
        <div className="card-thumb">
            {src && !failed ? (
                <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
            ) : (
                <span className="card-thumb-empty" aria-hidden="true"><FaBookOpen /></span>
            )}
            {kind && <span className="chip card-thumb-chip">{kind}</span>}
        </div>
    );
};

/**
 * `to` OR `onOpen` decides how the whole card behaves:
 *   - `onOpen`  → the card is a BUTTON that opens the item in a modal (updates, policies).
 *   - `to`      → the card is a LINK that navigates to a reading page (blog).
 *
 * Either way the ENTIRE card is the single clickable target (`.card-cover` stretched over
 * it), so the cursor is a pointer everywhere on the card — the title, the teaser and the
 * arrow alike — and there is exactly one tab stop. The heading is plain text; the cover
 * carries the accessible name.
 */
export const ContentCard = ({ to, onOpen, kind, title, teaser, meta, footer, image }) => (
    <article className={`card${image ? " card-has-media" : ""}`}>
        {onOpen ? (
            <button type="button" className="card-cover" onClick={onOpen} aria-label={title} />
        ) : (
            <Link to={to} className="card-cover" aria-label={title} />
        )}

        {image ? (
            <CardThumb src={image.src} kind={kind} />
        ) : (
            kind && <span className={`chip chip-${kind.toLowerCase()}`}>{kind}</span>
        )}

        <h3 className="card-title">{title}</h3>

        {teaser && <p className="card-teaser">{teaser}</p>}

        <div className="card-foot">
            {meta && <span className="card-meta">{meta}</span>}
            <span className="card-cta" aria-hidden="true">
                {footer || "Read more"} <span className="card-arrow">→</span>
            </span>
        </div>
    </article>
);

/* ─── EMPTY STATE ─────────────────────────────────────────────── */
/**
 * A section with nothing in it is a NORMAL state here, not an error: a visitor
 * legitimately sees only 'general' content, and may have zero policies. So this has
 * to read as designed and calm, never as breakage.
 */
export const EmptyState = ({ icon = "📭", title, message, action }) => (
    <div className="empty">
        <span className="empty-icon" aria-hidden="true">{icon}</span>
        <h3 className="empty-title">{title}</h3>
        <p className="empty-msg">{message}</p>
        {action}
    </div>
);

/* ─── SKELETONS ───────────────────────────────────────────────── */
/* Shown while content loads — a shimmering placeholder in the shape of the thing
   that is coming, rather than a spinner on a blank page. */

export const SkeletonCard = () => (
    <div className="card card-skeleton" aria-hidden="true">
        <span className="sk sk-chip" />
        <span className="sk sk-line sk-title" />
        <span className="sk sk-line" />
        <span className="sk sk-line" />
        <span className="sk sk-line sk-short" />
        <div className="card-foot">
            <span className="sk sk-line sk-meta" />
        </div>
    </div>
);

export const CardGridSkeleton = ({ count = 3, className = "" }) => (
    <div className={`card-grid ${className}`.trim()} aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading…</span>
        {Array.from({ length: count }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
);

export const SkeletonArticle = () => (
    <div aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading…</span>
        <span className="sk sk-line sk-heading" aria-hidden="true" />
        <span className="sk sk-line sk-meta" aria-hidden="true" />
        <div className="sk-body" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className={`sk sk-line${i % 4 === 3 ? " sk-short" : ""}`} />
            ))}
        </div>
    </div>
);
